import React, { useEffect, useState } from 'react';
import type { ModalComponentProps } from '@components/modals/constants';
import { ConfirmDialog } from '@components/primitives/ConfirmDialog';
import { useTelehealthAccess } from '@features/telehealth';
import { useCan, useOrganizationId } from '@features/user';
import AddIcon from '@mui/icons-material/Add';
import ArticleIcon from '@mui/icons-material/Article';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ScheduleIcon from '@mui/icons-material/Schedule';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { useTemplateNameById } from '../hooks';
import {
  useDeleteOrganizationAppointmentType,
  useOrganizationAppointmentTypes,
} from '../store/hooks';
import { type AppointmentType } from '../types';
import { resolveVisitMode } from '../utils/visitMode';

import AppointmentTypeModal from './AppointmentTypeModal';
import { ColorDot } from './ColorDot';

type ManagerPage = 'list' | 'create' | 'edit';

const formatDuration = (duration?: number | null) =>
  duration ? `${duration} min` : 'No default duration';

const formatModality = (type: AppointmentType, telehealthEnabled: boolean) => {
  const { modality, jotMeet } = resolveVisitMode(
    type.default_modality ?? null,
    type.telehealth ?? false,
    telehealthEnabled,
  );
  if (modality === 'video') return jotMeet ? 'Video + JotMeet' : 'Video';
  if (modality === 'audio') return 'Phone';
  if (modality === 'in_person') return 'In-person';
  return 'Visit mode not set';
};

const formatWorkflow = (type: AppointmentType) => {
  const forms = type.default_form_rules?.length ?? 0;
  const reminders = type.default_reminder_rules?.length ?? 0;
  if (!forms && !reminders) return 'None';

  return [
    forms ? `${forms} form${forms === 1 ? '' : 's'}` : null,
    reminders ? `${reminders} reminder${reminders === 1 ? '' : 's'}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
};

const readableTemplateName = (type: AppointmentType, templateNameById: Map<string, string>) => {
  if (!type.template_id) return 'No note template';
  return templateNameById.get(type.template_id) ?? 'Note template selected';
};

export const AppointmentTypesManagementModal: React.FC<ModalComponentProps> = ({
  closeModal,
  modalProps,
}) => {
  const organizationId = useOrganizationId();
  const canManageAppointmentTypes = useCan('manage_appointment_types');
  const { hasAccess: hasTelehealthAccess } = useTelehealthAccess();
  const { appointment } = useEventTracking();
  const { appointmentTypes, isLoading, error } = useOrganizationAppointmentTypes(
    organizationId,
    undefined,
    {
      skip: !canManageAppointmentTypes,
    },
  );
  const { deleteAppointmentType, isDeleting } =
    useDeleteOrganizationAppointmentType(organizationId);

  const [page, setPage] = useState<ManagerPage>('list');
  const [selectedType, setSelectedType] = useState<AppointmentType | null>(null);
  const [typeToDelete, setTypeToDelete] = useState<AppointmentType | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const source =
    typeof modalProps?.source === 'string' ? modalProps.source : 'appointment_types_manager';

  const templateNameById = useTemplateNameById({ skip: !canManageAppointmentTypes });

  useEffect(() => {
    appointment.typesManagementModalOpened({
      source,
      appointment_types_count: appointmentTypes.length,
    });
  }, [appointment, appointmentTypes.length, source]);

  const handleDelete = async () => {
    if (!typeToDelete) return;
    setDeleteError(null);
    const result = await deleteAppointmentType(typeToDelete.encounter_type_id);
    if (result.success) {
      setTypeToDelete(null);
      return;
    }
    setDeleteError(result.message || 'Appointment type could not be deleted. Please try again.');
  };

  const showList = () => {
    setPage('list');
    setSelectedType(null);
  };

  if (page === 'create') {
    return (
      <AppointmentTypeModal
        open
        inline
        onBack={showList}
        onDismiss={closeModal}
        onClose={showList}
        type="create"
        scope="organization"
        organizationId={organizationId}
        existingAppointmentTypes={appointmentTypes}
      />
    );
  }

  if (page === 'edit' && selectedType) {
    return (
      <AppointmentTypeModal
        open
        inline
        onBack={showList}
        onDismiss={closeModal}
        onClose={showList}
        type="edit"
        appointmentType={selectedType}
        scope="organization"
        organizationId={organizationId}
        canDelete
        existingAppointmentTypes={appointmentTypes}
      />
    );
  }

  return (
    <>
      <DialogTitle sx={{ pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Appointment Types
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 620 }}>
              Set the choices your team sees when scheduling and preparing encounters. Each type can
              carry its own color, duration, note template, forms, and reminders.
            </Typography>
          </Box>
          <IconButton onClick={closeModal} size="small" aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {!canManageAppointmentTypes ? (
          <Box sx={{ px: 3, py: 2 }}>
            <Alert severity="info">
              You do not have permission to manage practice appointment types.
            </Alert>
          </Box>
        ) : error ? (
          <Box sx={{ px: 3, py: 2 }}>
            <Alert severity="error">
              Appointment types could not be loaded. Please refresh and try again.
            </Alert>
          </Box>
        ) : isLoading ? (
          <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        ) : appointmentTypes.length === 0 ? (
          <Box sx={{ px: 3, py: 6, textAlign: 'center' }}>
            <EventAvailableIcon sx={{ fontSize: 42, color: 'primary.main', mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Create the first appointment type
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 520, mx: 'auto' }}>
              Start with the visits your team schedules most often, like intake, follow-up, testing,
              or medication management.
            </Typography>
            <Button
              sx={{ mt: 3 }}
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setPage('create')}
            >
              Add Appointment Type
            </Button>
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: '62vh', overflowX: 'auto' }}>
            <Table stickyHeader size="small" sx={{ minWidth: 760 }}>
              <TableHead>
                <TableRow
                  sx={{
                    '& th': {
                      bgcolor: 'background.paper',
                      color: 'text.secondary',
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: 0.5,
                      lineHeight: 1,
                      textTransform: 'uppercase',
                      py: 1,
                    },
                  }}
                >
                  <TableCell>Appointment type</TableCell>
                  <TableCell>Scheduling</TableCell>
                  <TableCell>Charting</TableCell>
                  <TableCell>Patient workflow</TableCell>
                  <TableCell align="right" sx={{ width: 88 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {appointmentTypes.map(type => (
                  <TableRow
                    key={type.encounter_type_id}
                    hover
                    sx={{
                      cursor: 'pointer',
                      '&:last-child td': { borderBottom: 0 },
                    }}
                    onClick={() => {
                      setSelectedType(type);
                      setPage('edit');
                    }}
                  >
                    <TableCell sx={{ py: 1.35, verticalAlign: 'top' }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                        <Box sx={{ pt: 0.25 }}>
                          <ColorDot colorEnum={type.color} size={16} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: 13,
                              fontWeight: 650,
                              lineHeight: 1.3,
                              overflowWrap: 'anywhere',
                            }}
                          >
                            {type.encounter_type}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', fontSize: 11 }}
                          >
                            Practice-wide
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 1.35, verticalAlign: 'top' }}>
                      <PropertyPreview
                        rows={[
                          {
                            key: 'duration',
                            icon: <ScheduleIcon fontSize="inherit" />,
                            value: formatDuration(type.default_duration),
                          },
                          {
                            key: 'visit-mode',
                            icon: <VideoCallIcon fontSize="inherit" />,
                            value: formatModality(type, hasTelehealthAccess),
                          },
                        ]}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 1.35, verticalAlign: 'top' }}>
                      <PropertyPreview
                        rows={[
                          {
                            key: 'template',
                            icon: <ArticleIcon fontSize="inherit" />,
                            value: readableTemplateName(type, templateNameById),
                          },
                        ]}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 1.35, verticalAlign: 'top' }}>
                      <PropertyPreview
                        rows={[
                          {
                            key: 'workflow',
                            icon: <AssignmentTurnedInIcon fontSize="inherit" />,
                            value: formatWorkflow(type),
                          },
                        ]}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1, verticalAlign: 'top' }}>
                      <Box
                        onClick={event => event.stopPropagation()}
                        sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}
                      >
                        <Tooltip title="Edit appointment type">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedType(type);
                              setPage('edit');
                            }}
                            aria-label={`Edit ${type.encounter_type}`}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete appointment type">
                          <IconButton
                            size="small"
                            onClick={() => setTypeToDelete(type)}
                            aria-label={`Delete ${type.encounter_type}`}
                            disabled={isDeleting}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      {deleteError && (
        <Box sx={{ px: 3, pt: 2 }}>
          <Alert severity="error">{deleteError}</Alert>
        </Box>
      )}

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button variant="text" onClick={closeModal}>
          Done
        </Button>
        {canManageAppointmentTypes && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setPage('create')}>
            Add Appointment Type
          </Button>
        )}
      </DialogActions>

      <ConfirmDialog
        open={!!typeToDelete}
        variant="warning"
        title="Delete Appointment Type?"
        message={`Delete "${typeToDelete?.encounter_type}"? It will no longer be available as a practice-wide scheduling option.`}
        onClose={() => setTypeToDelete(null)}
        onConfirm={handleDelete}
        confirmText="Delete"
        cancelText="Cancel"
        loading={isDeleting}
      />
    </>
  );
};

const PropertyPreview: React.FC<{
  rows: Array<{
    key: string;
    icon: React.ReactElement;
    value: string;
  }>;
}> = ({ rows }) => (
  <Box sx={{ display: 'grid', gap: 0.45, minWidth: 0 }}>
    {rows.map(row => (
      <Box
        key={row.key}
        sx={{
          display: 'grid',
          gridTemplateColumns: 'auto minmax(0, 1fr)',
          alignItems: 'center',
          gap: 0.75,
          minWidth: 0,
        }}
      >
        <Box sx={{ display: 'flex', fontSize: 14, flexShrink: 0, color: 'text.secondary' }}>
          {row.icon}
        </Box>
        <Typography
          variant="body2"
          sx={{
            color:
              row.value === 'None' ||
              row.value.startsWith('No ') ||
              row.value.endsWith('not set')
                ? 'text.secondary'
                : 'text.primary',
            fontSize: 12.5,
            lineHeight: 1.3,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={row.value}
        >
          {row.value}
        </Typography>
      </Box>
    ))}
  </Box>
);

export default AppointmentTypesManagementModal;
